import React, { useEffect, useRef, useState } from "react";
import _ from "lodash";
import Select from "react-select";
import {
  Carousel,
  CarouselItem,
  CarouselControl,
  Table,
  Button,
  Spinner,
  Form,
  FormFeedback,
} from "reactstrap";

import { useNavigate, useParams } from "react-router-dom";

import { getCutlist, getLiteProduct } from "./service";

import {
  Option,
  ProductPersonalizeContainer,
  ProductPersonalizeInfoContainer,
  ProductImgContainer,
  Thumbnail,
  ThumbnailContainer,
  ProductCustomizableContainer,
} from "./style";

import {
  CoreContainer,
  DimensionInput,
  Icon,
  Notification,
} from "../common/style";
import { MobileCutlist } from "../carpenter-view";
import { isMobileOnly } from "react-device-detect";
import { ProjectSettingsFormGroup } from "../project/style";

function ProductPersonalize() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [cutlists, setCutlists] = useState([]);
  const [dimensions, setDimensions] = useState({
    height: "",
    width: "",
    depth: "",
  });
  const [dimensionsRange, setDimensionsRange] = useState(null);
  const [disableSave, setDisableSave] = useState(false);
  const [expo, setExpo] = useState({
    top: false,
    right: false,
    bottom: false,
    left: false,
  });
  const [thickness, setThickness] = useState({
    carcass_thickness: null,
    backsheet_thickness: null,
    shutter_thickness: null,
    build_type: "",
  });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({
    type: "",
    text: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isCutlistLoading, setIsCutlistLoading] = useState(false);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const loadProduct = () => {
    setIsLoading(true);
    getLiteProduct({ productId })
      .then((product) => {
        setProduct(product);

        const selectedOptions = product.property_list.reduce((acc, prop) => {
          const selectedOption = prop.options.find((option) => option.selected);
          acc[prop.identifier] =
            prop.identifier === "build_type"
              ? selectedOption.identifier
              : selectedOption;
          return acc;
        }, {});

        const { height, width, depth } = product.dimensions;
        const {
          carcass_thickness,
          backsheet_thickness,
          shutter_thickness,
          build_type,
        } = selectedOptions;

        setDimensions({ height, width, depth });
        setThickness({
          carcass_thickness,
          backsheet_thickness,
          shutter_thickness,
          build_type,
        });
        setDimensionsRange(product.dimensions_range);

        fetchCutlist({
          height: height,
          width: width,
          depth: depth,
          exposed_side_top: "exposed_side_top_false",
          exposed_side_bottom: "exposed_side_bottom_false",
          exposed_side_right: "exposed_side_right_false",
          exposed_side_left: "exposed_side_left_false",
          exposed_side_back: "exposed_side_back_false",
          carcass_thickness: carcass_thickness.identifier,
          backsheet_thickness: backsheet_thickness.identifier,
          shutter_thickness: shutter_thickness.identifier,
          build_type: build_type,
        });
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotificationMessage({ type: "danger", text: error });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchCutlist = (payload) => {
    setIsCutlistLoading(true);
    getCutlist({
      productId: productId,
      options: payload,
    })
      .then((cutlist) => {
        setCutlists(Object.values(cutlist).flat());
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotificationMessage({ type: "danger", text: error });
      })
      .finally(() => {
        setIsCutlistLoading(false);
      });
  };

  useEffect(() => {
    loadProduct();
  }, [productId]);

  useEffect(() => {
    if (dimensionsRange) {
      const {
        min_height,
        max_height,
        min_width,
        max_width,
        min_depth,
        max_depth,
      } = dimensionsRange;

      setDisableSave(
        isDimensionNotValid(dimensions.height, {
          min_value: min_height,
          max_value: max_height,
        }) ||
          isDimensionNotValid(dimensions.width, {
            min_value: min_width,
            max_value: max_width,
          }) ||
          isDimensionNotValid(dimensions.depth, {
            min_value: min_depth,
            max_value: max_depth,
          })
      );
    }
  }, [dimensions]);

  const changeOptions = (
    setState,
    stateObject,
    selectedOption,
    rootIdentifier
  ) => {
    setState({
      ...stateObject,
      [rootIdentifier]: selectedOption,
    });
  };

  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  const next = (index) => {
    if (activeIndex === index) return;
    if (animating) return;

    const nextIndex =
      activeIndex === product.images.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
  };

  const previous = () => {
    if (animating) return;
    const nextIndex =
      activeIndex === 0 ? product.images.length - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
  };

  let slides = null;

  if (product) {
    slides = product.images.map((image, index) => (
      <CarouselItem
        key={index}
        onExiting={() => setAnimating(true)}
        onExited={() => setAnimating(false)}
      >
        <img src={image} className="mw-100 mh-100" />
      </CarouselItem>
    ));
  }

  const renderCarousel = () => {
    return (
      <Carousel
        interval={false}
        activeIndex={activeIndex}
        next={next}
        previous={previous}
        slide={false}
      >
        {slides}
        {slides.length > 1 && (
          <>
            <CarouselControl
              direction="prev"
              directionText="Previous"
              onClickHandler={previous}
            />
            <CarouselControl
              direction="next"
              directionText="Next"
              onClickHandler={next}
            />
          </>
        )}
      </Carousel>
    );
  };

  const [copied, setCopied] = useState(false);

  const tableRef = useRef(null);
  const [copiedContent, setCopiedContent] = useState("");

  const copyTableData = () => {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(tableRef.current);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
    selection.removeAllRanges();
    setCopiedContent(window.getSelection().toString());
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const dimensionsIdentifier = ["height", "width", "depth"];
  const thicknessIdentifier = [
    "carcass_thickness",
    "backsheet_thickness",
    "shutter_thickness",
  ];
  const expo_sides = ["left", "right", "top", "bottom"];

  const updateDimension = (dimension_key, dimension_range) => {
    const dimension_value = dimensions[dimension_key];
    const old_dimension_value = product.dimensions[dimension_key];
    const valid = isDimensionNotValid(dimension_value, dimension_range);

    if (
      dimension_value == old_dimension_value ||
      dimension_value == "" ||
      valid
    )
      setDimensions({
        ...dimensions,
        [dimension_key]: old_dimension_value,
      });
  };

  const isDimensionNotValid = (dimension, dimension_range) => {
    return (
      dimension.length > 0 &&
      (!/^\d*$/.test(dimension) ||
        parseInt(dimension) < dimension_range.min_value ||
        parseInt(dimension) > dimension_range.max_value)
    );
  };

  return (
    <>
      {notificationMessage.text && (
        <Notification
          color={notificationMessage.type}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notificationMessage.text}
        </Notification>
      )}
      {isLoading ? (
        <CoreContainer>
          <Spinner>Loading...</Spinner>
        </CoreContainer>
      ) : product ? (
        <ProductPersonalizeContainer className="product-personalize-container">
          <ProductPersonalizeInfoContainer>
            <div>
              <div
                className={`text-center ${
                  isMobileOnly ? "d-flex column-gap-4" : ""
                }`}
              >
                {isMobileOnly && (
                  <Icon
                    onClick={() => navigate("/modules")}
                    className="fa-solid fa-arrow-left mt-1"
                    size="20px"
                  />
                )}
                <h5 className="m-0" style={{ color: "gray" }}>
                  {product.name}
                </h5>
              </div>
              <ProductImgContainer className="d-flex justify-content-center align-items-center mt-3 mb-4">
                {renderCarousel()}
              </ProductImgContainer>
              <ThumbnailContainer
                mobileview="false"
                className="d-flex justify-content-center mb-4"
              >
                {product.images.map((image, index) => (
                  <Thumbnail
                    onClick={() => next(index)}
                    className={`pointer d-flex justify-content-center align-items-center p-1 ${
                      index === activeIndex ? "selected-thumbnail" : ""
                    }`}
                    key={index}
                  >
                    <img className="mh-100 mw-100" src={image} />
                  </Thumbnail>
                ))}
              </ThumbnailContainer>
            </div>
          </ProductPersonalizeInfoContainer>
          <ProductCustomizableContainer>
            <div
              style={{ boxShadow: "0 0 10px rgba(0, 0, 0, 0.2" }}
              className="d-flex column-gap-5 align-items-end flex-wrap row-gap-3 mb-4 p-3"
            >
              <div>
                <Form className="d-flex gap-5">
                  {dimensionsIdentifier.map((dimension, index) => (
                    <ProjectSettingsFormGroup key={index}>
                      <p className="m-0 sub-property-text text-capitalize">
                        {dimension}
                      </p>
                      <DimensionInput
                        value={dimensions[dimension]}
                        onChange={(ev) =>
                          setDimensions({
                            ...dimensions,
                            [dimension]: ev.target.value,
                          })
                        }
                        className="mt-2"
                        invalid={isDimensionNotValid(dimensions[dimension], {
                          min_value: dimensionsRange["min_" + dimension],
                          max_value: dimensionsRange["max_" + dimension],
                        })}
                        onBlur={() =>
                          updateDimension(dimension, {
                            min_value: dimensionsRange["min_" + dimension],
                            max_value: dimensionsRange["max_" + dimension],
                          })
                        }
                      />
                      <FormFeedback invalid>
                        {`Range: ${dimensionsRange["min_" + dimension]} to ${
                          dimensionsRange["max_" + dimension]
                        }`}
                      </FormFeedback>
                    </ProjectSettingsFormGroup>
                  ))}
                </Form>
              </div>
              <div>
                <p className="mb-1 sub-property-text">Expo</p>
                <div className="rounded-1 btn-group">
                  {expo_sides.map((side, index) => (
                    <Option
                      key={index}
                      onClick={() => {
                        changeOptions(setExpo, expo, !expo[side], side);
                      }}
                      className={`default-option text-capitalize ${
                        expo[side] && "selected-option"
                      }`}
                    >
                      {side}
                    </Option>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1 sub-property-text">Build Type</p>
                <div className="d-flex gap-3 flex-wrap">
                  {product.property_list
                    .find((e) => e.identifier === "build_type")
                    .options.map((option, index) => (
                      <Option
                        key={index}
                        onClick={() => {
                          changeOptions(
                            setThickness,
                            thickness,
                            option.identifier,
                            "build_type"
                          );
                        }}
                        className={`rounded-1 default-option ${
                          thickness["build_type"] === option.identifier &&
                          "selected-option"
                        }`}
                      >
                        {option.name}
                      </Option>
                    ))}
                </div>
              </div>
              <div>
                <div className="d-flex column-gap-3">
                  {thicknessIdentifier.map((id, index) => {
                    const property = product.property_list.find(
                      (e) => e.identifier === id
                    );
                    return (
                      <div key={index}>
                        <p className="sub-property-text mb-1">
                          {property.name.replace(" Thickness", "")}
                        </p>
                        <Select
                          value={thickness[property.identifier]}
                          options={property.options.map((option) => {
                            option.value = option.name;
                            option.label = option.name;
                            return option;
                          })}
                          onChange={(selectedOption) => {
                            changeOptions(
                              setThickness,
                              thickness,
                              selectedOption,
                              property.identifier
                            );
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <Button
                  disabled={disableSave}
                  onClick={() => {
                    fetchCutlist({
                      height: dimensions["height"],
                      width: dimensions["width"],
                      depth: dimensions["depth"],
                      exposed_side_top: "exposed_side_top_" + expo["top"],
                      exposed_side_bottom:
                        "exposed_side_bottom_" + expo["bottom"],
                      exposed_side_right: "exposed_side_right_" + expo["right"],
                      exposed_side_left: "exposed_side_left_" + expo["left"],
                      exposed_side_back: "exposed_side_back_" + expo["back"],
                      carcass_thickness:
                        thickness["carcass_thickness"].identifier,
                      backsheet_thickness:
                        thickness["backsheet_thickness"].identifier,
                      shutter_thickness:
                        thickness["shutter_thickness"].identifier,
                      build_type: thickness["build_type"],
                    });
                  }}
                  style={{ padding: "8px 10px" }}
                  className="bg-success border-0"
                >
                  Update Cutlist
                </Button>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              {copied ? (
                <div className="d-flex align-items-center">
                  <i className="fa-solid fa-check me-2"></i>
                  <p className="m-0">Copied</p>
                </div>
              ) : (
                <div
                  onClick={copyTableData}
                  className="d-flex align-items-center pointer"
                >
                  <i className="fa-regular fa-clipboard me-2"></i>
                  <p className="m-0">Copy</p>
                </div>
              )}
            </div>
            {isCutlistLoading ? (
              <CoreContainer style={{ height: "22vh" }}>
                <Spinner>Loading...</Spinner>
              </CoreContainer>
            ) : (
              <div className="overflow-hidden">
                <div style={{ height: "0px" }} className="opacity-0">
                  <Table innerRef={tableRef}>
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Name</th>
                        <th>Length1</th>
                        <th>Length2</th>
                        <th>Groove</th>
                        <th>Edge Config</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cutlists.map((cutlist, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{cutlist.name}</td>
                          <td>{cutlist.length_1}</td>
                          <td>{cutlist.length_2}</td>
                          <td>{cutlist.groove_config}</td>
                          <td>{cutlist.edge_config}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <div className="mt-3 mb-4 border border-2">
                  {cutlists &&
                    cutlists.map((e, index) => (
                      <MobileCutlist
                        showcolor={false}
                        key={index}
                        cutlist={e}
                        index={index}
                      />
                    ))}
                </div>
              </div>
            )}
          </ProductCustomizableContainer>
        </ProductPersonalizeContainer>
      ) : null}
    </>
  );
}

export default ProductPersonalize;

// TODO: Form selected options in backend
